import {
  getDietaryIngredients,
  useCustomerDietaryRestrictions,
} from '@hungryroot/dietary-restrictions';
import {
  DynamicConfigs,
  DynamicConfigTypes,
  getDynamicConfigValue,
  useDynamicConfigs,
} from '@hungryroot/dynamic-configs';
import { Footer } from '@hungryroot/rdp';
import { type ProductDetail } from '@hungryroot/rest';
import { type ProductV2 } from '@hungryroot/rest/api/v2/products/get';
import { segmentAnalytics } from '@hungryroot/segment';
import { maxSameItem } from '../../frontend/react/src/customize/const';
import { getCount } from '../cards/ProductCard/BindToApplicationProductCard';
import { isNotAddable, type IsNotAddableParams } from '../cart/query';
import { convertProductV2ToProduct } from '../cart/transforms';
import { useCustomization } from '../CustomizationProvider/CustomizationProvider';

type ProductMutation = (product?: ProductDetail) => void;

export type ExtraHandlers = {
  onAdd?: () => void;
  onIncrement?: () => void;
  onRemove?: () => void;
  onSubtract?: () => void;
};

export function usePdpFooter(extraHandlers: ExtraHandlers = {}) {
  const { data: dietaryRestrictionsData } = useCustomerDietaryRestrictions();
  const customization = useCustomization();

  const mutations =
    'mutations' in customization ? customization.mutations : undefined;

  const cart = 'cart' in customization ? customization.cart : undefined;

  const removeProduct: ProductMutation = (product) => {
    if (mutations && product) {
      mutations.removeProduct(convertProductV2ToProduct(product as ProductV2));
    }
  };

  const addProduct: ProductMutation = (product) => {
    const dietaryProductIngredients = product
      ? getDietaryIngredients(
          product.tags,
          dietaryRestrictionsData?.dietary_restrictions || []
        )
      : undefined;

    if (mutations && product) {
      mutations.addProduct({
        product: convertProductV2ToProduct(product as ProductV2),
        dietaryProductIngredients,
      });
    }
  };

  const handleAddClick: ProductMutation = (product) => {
    addProduct(product);
    extraHandlers.onAdd?.();
  };

  const handleIncrementClick: ProductMutation = (product) => {
    addProduct(product);
    extraHandlers.onIncrement?.();
  };

  const handleRemoveClick: ProductMutation = (product) => {
    removeProduct(product);
    extraHandlers.onRemove?.();
  };

  const handleSubtractClick: ProductMutation = (product) => {
    removeProduct(product);
    extraHandlers.onSubtract?.();
  };

  const { data: dynamicConfigs } = useDynamicConfigs({
    name: [DynamicConfigTypes.Customize],
  });

  const getIsDisabled = (product: ProductDetail) => {
    if (!('cart' in customization)) {
      return true;
    }

    return isNotAddable({
      cart: customization.cart,
      maxSameItem,
      volumeLimit: dynamicConfigs
        ? getDynamicConfigValue(
            dynamicConfigs.results,
            DynamicConfigs.CustomizeVolumeLimit
          )
        : undefined,
      coldVolumeLimit: dynamicConfigs
        ? getDynamicConfigValue(
            dynamicConfigs.results,
            DynamicConfigs.CustomizeColdVolumeLimit
          )
        : undefined,
      pairing: undefined,
      product: product,
    } as IsNotAddableParams);
  };

  const loading = !!mutations?.isMutating;

  const createFooterButton = (
    product?: ProductDetail,
    handlers?: ExtraHandlers
  ) => {
    const withProduct = (fn: ProductMutation) => () => {
      fn(product);
    };

    const isDisabled = product ? getIsDisabled(product) : false;

    return product && cart ? (
      <Footer
        onAddClick={withProduct((product) => {
          handleAddClick(product);
          handlers?.onAdd?.();

          if (cart.order_id && product) {
            sendInteractionEvent({
              orderId: cart.order_id,
              id: product.id,
              name: product.name,
              interactionType: 'add_to_cart',
            });
          }
        })}
        onIncrementClick={withProduct((product) => {
          handleIncrementClick(product);
          handlers?.onIncrement?.();

          if (cart.order_id && product) {
            sendInteractionEvent({
              orderId: cart.order_id,
              id: product.id,
              name: product.name,
              interactionType: 'add_to_cart',
            });
          }
        })}
        onRemoveClick={withProduct((product) => {
          handleRemoveClick(product);
          handlers?.onRemove?.();

          if (cart.order_id && product) {
            sendInteractionEvent({
              orderId: cart.order_id,
              id: product.id,
              name: product.name,
              interactionType: 'remove_from_cart',
            });
          }
        })}
        onSubtractClick={withProduct((product) => {
          handleSubtractClick(product);
          handlers?.onSubtract?.();

          if (cart.order_id && product) {
            sendInteractionEvent({
              orderId: cart.order_id,
              id: product.id,
              name: product.name,
              interactionType: 'remove_from_cart',
            });
          }
        })}
        creditsForItem={Number(product.points)}
        numInCart={getCount(product.id, false, { cart })}
        isLoading={loading}
        isDisabled={isDisabled}
        outOfStock={product.is_merchandized === false}
      />
    ) : null;
  };

  return { createFooterButton };
}

type TriggerInteractionEventArgs = {
  orderId: number;
  id: number;
  name: string;
  interactionType: 'add_to_cart' | 'remove_from_cart';
};

function sendInteractionEvent({
  orderId,
  id,
  name,
  interactionType,
}: TriggerInteractionEventArgs) {
  segmentAnalytics.track('Product Interaction', {
    event_name: 'Product Interaction',
    order_id: orderId,
    product_id: id,
    product_name: name,
    interaction_type: interactionType,
    page_name: 'PDP',
  });
}
